import { Injectable } from '@angular/core';
import { Category } from '../../models/interfaces/category.interface';
import { categoriesMock } from '../../mocks/interfaces/category.mock';
import { PaginationDto } from '../pagination/models/interfaces/pagination-dto';
import { createCrudUrl, CrudRequestParams } from '../pagination/models/interfaces/crud';
import { Api } from '../api/api';
import { isFakeBackendEnabled } from '../../utils/fake-backend.util';

export interface CategoryPayload {
  name: string;
  description?: string;
  shortDescription?: string;
  color?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CategoryService {

  private readonly api: Api = new Api();

  private readonly baseRoute = '/categories';

  public async getManyMocked(params?: CrudRequestParams<Category>): Promise<PaginationDto<Category>> {
    const page = Math.max(1, params?.page ?? 1);
    const limit = Math.max(1, params?.limit ?? 10);
    const search = this.getSearchValue(params?.search);

    const filtered = categoriesMock.filter((category) => {
      if (!search) {
        return true;
      }

      return category.name.toLowerCase().includes(search)
        || (category.description || '').toLowerCase().includes(search);
    });

    const sortRule = params?.sort?.[0];
    const sorted = [...filtered].sort((a, b) => {
      if (!sortRule?.field) {
        return 0;
      }

      const direction = sortRule.order === 'ASC' ? 1 : -1;
      const field = sortRule.field as keyof Category;
      const left = String(a[field] ?? '');
      const right = String(b[field] ?? '');
      return left.localeCompare(right) * direction;
    });

    const total = sorted.length;
    const pageCount = Math.max(1, Math.ceil(total / limit));
    const safePage = Math.min(page, pageCount);
    const start = (safePage - 1) * limit;
    const data = sorted.slice(start, start + limit);

    return {
      data,
      page: safePage,
      pageCount,
      count: data.length,
      total,
    };
  }

  public async createMocked(payload: CategoryPayload): Promise<Category> {
    const name = payload.name.trim();

    if (!name) {
      throw new Error('Nome da categoria é obrigatório.');
    }

    const now = new Date();

    const created: Category = {
      id: `category-${crypto.randomUUID()}`,
      tenantId: 'tenant-uuid',
      isActive: true,
      name,
      description: payload.description?.trim() || undefined,
      shortDescription: payload.shortDescription?.trim() || undefined,
      color: this.normalizeColor(payload.color),
      createdAt: now,
      updatedAt: now,
    };

    categoriesMock.unshift(created);

    return created;
  }

  public async updateMocked(categoryId: string, payload: CategoryPayload): Promise<Category> {
    const targetIndex = categoriesMock.findIndex(category => category.id === categoryId);

    if (targetIndex < 0) {
      throw new Error('Categoria não encontrada para edição.');
    }

    const current = categoriesMock[targetIndex];
    const nextName = payload.name.trim();

    if (!nextName) {
      throw new Error('Nome da categoria é obrigatório.');
    }

    const updated: Category = {
      ...current,
      name: nextName,
      description: payload.description?.trim() || undefined,
      shortDescription: payload.shortDescription === undefined
        ? current.shortDescription
        : payload.shortDescription.trim() || undefined,
      color: payload.color?.trim() ? this.normalizeColor(payload.color) : current.color,
      updatedAt: new Date(),
    };

    categoriesMock[targetIndex] = updated;

    return updated;
  }

  public async deleteMocked(categoryId: string): Promise<void> {
    const targetIndex = categoriesMock.findIndex((category) => category.id === categoryId);

    if (targetIndex < 0) {
      throw new Error('Categoria não encontrada.');
    }

    categoriesMock.splice(targetIndex, 1);
  }

  // ─── Public API (fake backend ou integração real) ─────────────────────────

  public async getMany(params?: CrudRequestParams<Category>): Promise<PaginationDto<Category>> {
    if (isFakeBackendEnabled()) {
      return this.getManyMocked(params);
    }

    const url = createCrudUrl<Category>(this.baseRoute, params);
    const response = await this.api.getAsync<PaginationDto<Category>>(url);

    if (!response) {
      return {
        data: [],
        page: 0,
        pageCount: 0,
        count: 0,
        total: 0,
      };
    }

    return response;
  }

  public async create(payload: CategoryPayload): Promise<Category> {
    if (isFakeBackendEnabled()) {
      return this.createMocked(payload);
    }

    const response = await this.api.postAsync<Category>(this.baseRoute, payload);

    if (!response) {
      throw new Error('Falha ao criar a categoria.');
    }

    return response;
  }

  public async update(categoryId: string, payload: CategoryPayload): Promise<Category> {
    if (isFakeBackendEnabled()) {
      return this.updateMocked(categoryId, payload);
    }

    const response = await this.api.putAsync<Category>(`${this.baseRoute}/${categoryId}`, payload);

    if (!response) {
      throw new Error('Falha ao atualizar a categoria.');
    }

    return response;
  }

  public async delete(categoryId: string): Promise<void> {
    if (isFakeBackendEnabled()) {
      return this.deleteMocked(categoryId);
    }

    await this.api.deleteAsync(`${this.baseRoute}/${categoryId}`);
  }

  private getSearchValue(search?: CrudRequestParams<Category>['search']): string {
    if (!search) {
      return '';
    }

    return this.extractSearchFromCondition(search) || '';
  }

  private extractSearchFromCondition(condition: unknown): string | undefined {
    if (!condition || typeof condition !== 'object') {
      return undefined;
    }

    const record = condition as Record<string, unknown>;
    const fieldSearch = this.extractSearchFromField(record['name'])
      || this.extractSearchFromField(record['description']);

    if (fieldSearch) {
      return fieldSearch;
    }

    const orConditions = Array.isArray(record['$or']) ? record['$or'] as unknown[] : [];

    for (const nestedCondition of orConditions) {
      const nestedSearch = this.extractSearchFromCondition(nestedCondition);

      if (nestedSearch) {
        return nestedSearch;
      }
    }

    return undefined;
  }

  private extractSearchFromField(field: unknown): string | undefined {
    if (typeof field === 'string') {
      return field.toLowerCase().trim();
    }

    if (!field || typeof field !== 'object') {
      return undefined;
    }

    const operators = field as Record<string, unknown>;
    const value = operators['$contL'] ?? operators['$cont'];

    if (typeof value === 'string') {
      return value.toLowerCase().trim();
    }

    return undefined;
  }

  private normalizeColor(color?: string): string {
    const value = (color || '').trim();

    if (!value) {
      return '#5B55FF';
    }

    return value.startsWith('#') ? value : `#${value}`;
  }
}
